import { Component, Input } from "@angular/core";
import { IFilterOptions } from "@app/interfaces/filters/IFilterOptions";
import { IJourney } from "@app/interfaces/IJourney";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IGroupWithIds } from "@app/main/components/resourceManagement/groups/_models/IGroupWithIds";
import { IDashboardWidget } from "@app/main/modules/dashboard/_models/IDashboardWidget";
import { IGeofenceVisit } from "@app/services/geofence.service";
import { GroupService } from "@app/services/groups/group.service";
import { DateService } from "@app/services/date.service";
import { JourneyService } from "@app/services/journey.service";
import { IVehicleUtilisationRecord, UtilisationService } from "@app/services/utilisation.service";
import { VehicleService } from "@app/services/vehicle.service";
import * as moment from "moment";
import { IDayUtilisationMap } from "../../_models/IDayMap";
import './vehicle-utilisation-widget.component.scss';

@Component({
    selector: 'v-vehicle-utilisation-widget',
    template: require('./vehicle-utilisation-widget.component.html')
})
export class VVehicleUtilisationWidgetComponent {
    @Input() widget: IDashboardWidget;
    @Input() filterOptions: IFilterOptions;

    vehicles: IVehicle[];
    journeys: IJourney[];

    depotsWithHomeLocations: IGroupWithIds[];
    geofenceVisits: IGeofenceVisit[];

    dayMap: IDayUtilisationMap[] = [];

    loading: boolean;

    private hoursPerVerticalSegment = 4;

    constructor(
        private readonly dateService: DateService,
        private readonly vehicleService: VehicleService,
        private readonly groupService: GroupService,
        private readonly journeyService: JourneyService,
        private readonly utilisationService: UtilisationService,
    ) {
    }

    ngOnInit(): void {
        this.loadData();

        this.widget.onDataDefinitionUpdated = () => {
            setTimeout(() => {
                this.loadData();
            }, 0);
        };
    }

    //--

    private loadData() {
        this.loading = true;

        const vehiclesPromise = this.vehicleService.getVehicles({ includePeripheralMappings: true }) as Promise<IVehicle[]>;
        const journeysPromise = this.journeyService.getAllJourneys(this.filterOptions.dateFilter.start, this.filterOptions.dateFilter.end, false, false, true, false) as Promise<IJourney[]>;


        Promise
            .all([vehiclesPromise, journeysPromise])
            .then(([vehicles, journeys]) => {
                this.vehicles = vehicles;
                this.journeys = journeys;
                if (this.filterOptions) {

                    if (this.filterOptions.groupFilter && this.filterOptions.groupFilter.group) {
                        this.vehicles =
                            this.vehicles.filter(v => this.groupService.hasVehicle(this.filterOptions.groupFilter.group, v.Id));
                    }
                    if (this.filterOptions.vehicleFilter && this.filterOptions.vehicleFilter.vehicle) {
                        this.vehicles =
                            this.vehicles.filter(v => v.Id == this.filterOptions.vehicleFilter.vehicle.Id);
                        this.journeys = this.journeys.filter(v => v.VehicleId == this.filterOptions.vehicleFilter.vehicle.Id);
                    }
                }

                var utilisationRecords = this.utilisationService.createUtilisationRecordsForJourneys(
                    this.filterOptions.dateFilter.start,
                    this.filterOptions.dateFilter.end,
                    this.vehicles,
                    this.journeys);

                this.createDayMaps(utilisationRecords);
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private createEmptyDayMaps() {
        this.dayMap = [];

        for (let i = 0; i < 7; i++) {
            this.dayMap[i] = {
                day: i,
                dayString: this.dateService.getDayOfWeek(i, true),
                hourUtilisation: [],
                utilisationPeriods: []
            };
        }

    }

    private createDayMaps(vehicleUtilisationRecords: IVehicleUtilisationRecord[]) {

        this.createEmptyDayMaps();

        // Now create hour maps
        this.dayMap.forEach(dayMap => {
            var startOfDay = moment(this.filterOptions.dateFilter.start).add(dayMap.day, "d");

            for (let i = 0; i < 24 / this.hoursPerVerticalSegment; i++) {

                const currentHour = i * this.hoursPerVerticalSegment;
                const nextHour = currentHour + this.hoursPerVerticalSegment;
                var currentHourStart = startOfDay.clone().hour(currentHour).minute(0);
                var currentHourEnd = startOfDay.clone().hour(nextHour).minute(0);

                let vehiclesUtilised = 0;
                let vehiclesUtilisedPercentage = 0;

                vehicleUtilisationRecords.forEach(record => {

                    var utilised = record.utilisationRecords.some(period => {
                        const dayMatch = period.startMoment.day() <= dayMap.day &&
                            period.endMoment.day() >= dayMap.day;

                        let hourMatch: boolean;
                        if (nextHour >= 24) {
                            hourMatch = (period.startMoment.hour() >= currentHourStart.hour() && period.startMoment.hour() <= 23) ||
                                (period.endMoment.hour() >= currentHourStart.hour() && period.endMoment.hour() <= 23);
                        } else {
                            hourMatch = period.startMoment.hour() <= currentHourEnd.hour() &&
                                period.endMoment.hour() >= currentHourStart.hour();
                        }

                        return dayMatch && hourMatch;
                    });

                    if (utilised) {
                        vehiclesUtilised++;
                    }
                });

                vehiclesUtilisedPercentage = Math.round(vehiclesUtilised / this.vehicles.length * 100);

                dayMap.hourUtilisation.push({
                    hour: currentHour,
                    hourString: `${currentHour}:00`,
                    percentage: vehiclesUtilisedPercentage,
                    vehiclesMoved: vehiclesUtilised,
                    percentageClass: ""
                });
            }

            dayMap.hourUtilisation.reverse();
        });

        this.dayMap.forEach(dayMap => {
            dayMap.hourUtilisation.forEach(hour => {
                // Aim for a 0-10 scale
                var adjustedDensity = Math.round(hour.percentage / 10);
                hour.percentageClass = `density-${adjustedDensity}`;
            });
        });

        // Put sunday last
        const sunday = this.dayMap.splice(0, 1);
        this.dayMap.push(sunday[0]);
    }
}
